import React, {useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm, Validate} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {ErrorDisplay, LoadingSpinner} from "../../../common";
import {useObservable} from "../../../rxsupport";
import {Subject} from "rxjs";
import {ClinicalRecommendationInput} from "../form-components/auth-hours/ClinicalRecommendationInput";
import {ClientOfferInput} from "../form-components/auth-hours/ClientOfferInput";
import {ReportModal} from "../form-components/ReportModal";
import {
    AuthorizationInfo,
    defaultClinicalRecommendationHoursFromAuth,
    defaultServiceTypesByAuthTier1,
    ReportServiceType,
    serviceTypeOptionsAvailableForAllTiers,
    StaffingFormInput,
} from "../domain";
import {CommentsInput} from "../client-report/CommentsInput";
import {FatOutlineButton} from "../../../common/component/FatOutlineButton";
import {differenceInBusinessDays, addBusinessDays} from "date-fns";
import {showToast} from "common";
import _ from "lodash";
import {ModalDatePicker} from "../form-components/ModalDatePicker";
import {filterServiceTypesToAvailableOptions, LatestOfferedHourData, pickHoursForServiceTypes, useLatestOfferedHours,} from "./useLatestOfferedHours";
import {hasClinicalRecommendationMaxHours} from "../form-components/auth-hours/view-domains";

type StaffingPlanForm = LatestOfferedHourData & StaffingFormInput;

type Props = {
    authorization: AuthorizationInfo;
    visible$: Subject<boolean>;
    onFinished: (formValues: StaffingFormInput | null) => Promise<void>;
    isAuth?: boolean;
    treatmentHoldStaffingFlow?: boolean;
};

export function StaffingPlanFormModal({
    authorization,
    visible$,
    onFinished,
    isAuth = false,
    treatmentHoldStaffingFlow = false,
}: Props) {
    const authorizationGuid = authorization.guid;
    const restrictedOptions = useMemo(
        () => serviceTypeOptionsAvailableForAllTiers(authorization)?.options ?? null,
        [authorization.guid], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const defaultServiceTypes = useMemo(
        () => filterServiceTypesToAvailableOptions(defaultServiceTypesByAuthTier1(authorization), restrictedOptions),
        [authorization.guid, restrictedOptions], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const visible = useObservable(visible$, false);

    const methods = useForm<StaffingPlanForm>({
        defaultValues: {
            serviceType: defaultServiceTypes,
        }
    });
    const watchedServiceTypes = methods.watch('serviceType') as ReportServiceType[];
    const serviceTypesKey = JSON.stringify(watchedServiceTypes);

    useEffect(() => {
        methods.setValue('serviceType', defaultServiceTypes);
    }, [authorization.guid, defaultServiceTypes, methods]);

    useEffect(() => {
        const clinicalHours = pickHoursForServiceTypes(
            defaultClinicalRecommendationHoursFromAuth(authorization) as Record<string, number | null>,
            watchedServiceTypes,
            {hourRanges: true},
        );
        Object.entries(clinicalHours).forEach(([field, value]) => methods.setValue(field as keyof StaffingPlanForm, value));
    }, [authorization.guid, methods, serviceTypesKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const {data, isRunning$, error} = useLatestOfferedHours(visible, methods, authorizationGuid, watchedServiceTypes);

    const loading = useObservable(isRunning$, false);
    const {promiseInProgress} = usePromiseTracker();

    const isCallingBackend = loading || promiseInProgress;

    const skipPrompt = isAuth && !treatmentHoldStaffingFlow;

    const [userWantsToSubmit, setUserSubmit] = useState(skipPrompt);
    const setUserWantsToSubmit = useCallback((yes: boolean) => {
        setUserSubmit(skipPrompt || yes);
    }, [skipPrompt]);
    useEffect(() => {
        const sub = visible$.subscribe((isModalVisible) => {
            if (!isModalVisible) {
                setUserWantsToSubmit(false);
            } else if (treatmentHoldStaffingFlow) {
                setUserSubmit(false);
            }
        });
        return () => sub.unsubscribe();
    }, [visible$, setUserWantsToSubmit, treatmentHoldStaffingFlow]);

    const submitWithTracking = useCallback((formValues: StaffingFormInput | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const yesText = 'Submit';
    const noText = treatmentHoldStaffingFlow && !userWantsToSubmit ? 'Remind Me Later' : 'Cancel';
    const onRemindMeLater = useCallback(async () => {
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);
    const onCancel = useCallback(async () => {
        if (!isAuth && !userWantsToSubmit) {
            const in2BizDay = addBusinessDays(new Date(), 2).toDateString();
            showToast.warning(
                `Submission for Staffing Plan is cancelled. Staffing Plan needs to be submitted by ${in2BizDay}`,
                {duration: 15}
            );
        } else {
            showToast.warning('Submission for Staffing Plan is cancelled');
        }
        methods.reset();
        visible$.next(false);
    }, [methods, visible$, isAuth, userWantsToSubmit]);
    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!userWantsToSubmit) {
            setUserWantsToSubmit(true);
        } else {
            await methods.handleSubmit(async (data: StaffingPlanForm) => {
                await submitWithTracking(_.pick(data, [
                    'comments',
                    'fullOfferStartDate',
                    'reviewedAndApprovedByClientFamilyDate',
                    'offeredParaprofessionalHours',
                    'offeredMidLevelHours',
                    'offeredHighLevelHours',
                    'offeredGroupCareHours',
                    'currentOfferedParaprofessionalHours',
                    'currentOfferedMidLevelHours',
                    'currentOfferedHighLevelHours',
                    'currentOfferedGroupCareHours',
                    'serviceType',
                    'caregiverMidLevelHoursMin',
                    'caregiverMidLevelHoursMax',
                    'caregiverHighLevelHoursMin',
                    'caregiverHighLevelHoursMax',
                    'practitionerParaprofessionalHoursMin',
                    'practitionerParaprofessionalHoursMax',
                    'practitionerMidLevelHoursMin',
                    'practitionerMidLevelHoursMax',
                    'practitionerHighLevelHoursMin',
                    'practitionerHighLevelHoursMax',
                    'practitionerGroupCareHoursMin',
                    'practitionerGroupCareHoursMax',
                ]));
                methods.reset();
                visible$.next(false);
            })(e);
        }
    }, [methods, userWantsToSubmit, setUserWantsToSubmit, visible$, submitWithTracking]);

    const requireRationaleForOfferStartDate: Validate<string> = useCallback(async (comments) => {
        const offerStartDate = methods.getValues('fullOfferStartDate') || '';
        if (offerStartDate.length > 0) {
            const bizDays = differenceInBusinessDays(new Date(offerStartDate), new Date());
            if (bizDays > 10) {
                const rationale = (comments || '').trim();
                return rationale.length > 0 || 'Staffing Plan Rationale is required when Full-offer Start Date is more than 10 business days from today';
            }
        }
        return true;
    }, [methods]);

    if (error) {
        visible$.next(false);
        return <ErrorDisplay error={error} title="Error submitting staffing plan" />;
    }

    const isNew = !loading && data?.authorizationLatestOfferedHours == null;
    const clinicalHoursFromAuth = defaultClinicalRecommendationHoursFromAuth(authorization);
    const disabledForHours = hasClinicalRecommendationMaxHours(clinicalHoursFromAuth, watchedServiceTypes);

    return (
        <ReportModal visible={visible} title={'Submit Staffing Plan'} onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!isCallingBackend && (<>
                        <div className="space-y-6">
                            <div className="flex flex-col w-full p-4 scrollbar-always"
                                 style={{maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)"}}>

                                {!userWantsToSubmit && (<>
                                    <p>
                                        {treatmentHoldStaffingFlow
                                            ? 'Do you want to submit a staffing plan now or be reminded?'
                                            : 'Do you want to submit Staffing Plan now?'}
                                    </p>
                                </>)}

                                {userWantsToSubmit && (<>
                                    <ClinicalRecommendationInput
                                        disabled={isCallingBackend}
                                        disabledForHours={disabledForHours}
                                        restrictedOptions={restrictedOptions}
                                        reportType={methods.getValues('reportType')}
                                        isNewOfferForStaffingPlan={isNew}
                                        isForStaffingPlan
                                    />

                                    <ClientOfferInput disabled={isCallingBackend}
                                                      reportType={methods.getValues('reportType')}
                                                      isStaffingPlan
                                                      isClientOffer={false}
                                                      isNew={isNew}
                                    />

                                    <ClientOfferInput disabled={isCallingBackend}
                                                      reportType={methods.getValues('reportType')}
                                                      isStaffingPlan
                                                      isNew={isNew}
                                    />

                                    <div className='my-1'></div>

                                    <CommentsInput
                                        sending={isCallingBackend}
                                        validate={requireRationaleForOfferStartDate}
                                    />

                                    <ModalDatePicker<StaffingPlanForm>
                                        disabled={isCallingBackend}
                                        label={'Full Offer Start Date'}
                                        field={'fullOfferStartDate'}
                                        dependentFields={['comments']}
                                    />

                                    <ModalDatePicker<StaffingPlanForm>
                                        disabled={isCallingBackend}
                                        label={'Date Staffing Plan Reviewed by Client Family'}
                                        field={'reviewedAndApprovedByClientFamilyDate'}
                                    />
                                </>)}
                            </div>
                        </div>

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className="flex w-1/4">
                                <FatOutlineButton color={'red'}
                                                  text={noText}
                                                  onClick={treatmentHoldStaffingFlow && !userWantsToSubmit ? onRemindMeLater : onCancel}/>
                                <span style={{width: 10}}/>
                                <FatOutlineButton type="submit" text={yesText} color={"green"}/>
                            </div>
                        </div>
                    </>)}

                    {isCallingBackend &&
                        <div className="p-24">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    }
                </form>
            </FormProvider>
        </ReportModal>
    );
}
